import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { proveedoresAPI, tiposProveedoresAPI, provinciasAPI, localidadesAPI, usuariosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { ExportActions } from '../components/ExportActions';
import {
  exportFromColumnas,
  valorCeldaProveedorExport,
  imprimirListadoColumnas,
} from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';

const CLAVE_PREFERENCIAS = 'proveedores_listado';
const CLAVE_FILTROS_PROVEEDORES = 'proveedores_listado_filtros';
const COLUMNAS_DEFINICION = [
  { id: 'cuit', label: 'CUIT' },
  { id: 'razon_social', label: 'Razón Social' },
  { id: 'estado', label: 'Estado' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'direccion', label: 'Dirección' },
  { id: 'provincia', label: 'Provincia' },
  { id: 'localidad', label: 'Localidad' },
  { id: 'telefono', label: 'Teléfono' },
  { id: 'saldo', label: 'Saldo' },
];
const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultOrdenColumnas = COLUMNAS_DEFINICION.map(c => c.id);
const defaultFiltrosValores = {
  cuit: '', razon_social: '', razon_social_prefix: false, activo: 'true', tipo_id: '', direccion: '', provincia_id: '', localidad_id: '', telefono: '',
};

const getSortValue = (proveedor, columnId) => {
  switch (columnId) {
    case 'cuit': return (proveedor.cuit || '').toString().toLowerCase();
    case 'razon_social': return (proveedor.razon_social || '').toString().toLowerCase();
    case 'estado': return proveedor.activo === true ? 1 : 0;
    case 'tipo': return (proveedor.tipo_nombre || '').toString().toLowerCase();
    case 'direccion': return (proveedor.direccion || '').toString().toLowerCase();
    case 'provincia': return (proveedor.provincia_nombre || '').toString().toLowerCase();
    case 'localidad': return (proveedor.localidad_nombre || '').toString().toLowerCase();
    case 'telefono': return (proveedor.telefono || '').toString().toLowerCase();
    case 'saldo': return parseFloat(proveedor.saldo) || 0;
    default: return '';
  }
};

const Proveedores = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaProveedoresRef = useRef(true);
  const [refrescandoProveedores, setRefrescandoProveedores] = useState(false);
  const cargaProveedoresSeqRef = useRef(0);
  const [tipos, setTipos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [filtrosValores, setFiltrosValores] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CLAVE_FILTROS_PROVEEDORES);
      if (saved) {
        return { ...defaultFiltrosValores, ...JSON.parse(saved) };
      }
    } catch (e) {}
    return { ...defaultFiltrosValores };
  });
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnas]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);
  const { hasPermission } = useAuth();
  const { logoUrl } = useBranding();
  const { formatFecha, fechaHoy } = useZonaHoraria();

  const colVisible = (id) => columnasVisibles[id] !== false;
  const filtroVisible = useCallback((colId) => filtrosVisibles[colId] !== false && columnasVisibles[colId] !== false, [filtrosVisibles, columnasVisibles]);

  const setFiltro = (key, value) => setFiltrosValores(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    sessionStorage.setItem(CLAVE_FILTROS_PROVEEDORES, JSON.stringify(filtrosValores));
  }, [filtrosValores]);

  const loadProveedores = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaProveedoresRef.current;
    const seq = ++cargaProveedoresSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescandoProveedores(true);
      const params = {};
      if (filtroVisible('cuit') && filtrosValores.cuit) params.cuit = filtrosValores.cuit;
      if (filtroVisible('razon_social') && filtrosValores.razon_social) {
        params.razon_social = filtrosValores.razon_social;
        params.razon_social_modo = filtrosValores.razon_social_prefix ? 'prefix' : 'contains';
      }
      if (filtroVisible('estado')) params.activo = filtrosValores.activo;
      else params.activo = 'true';
      if (filtroVisible('tipo') && filtrosValores.tipo_id) params.tipo_id = filtrosValores.tipo_id;
      if (filtroVisible('direccion') && filtrosValores.direccion) params.direccion = filtrosValores.direccion;
      if (filtroVisible('provincia') && filtrosValores.provincia_id) params.provincia_id = filtrosValores.provincia_id;
      if (filtroVisible('localidad') && filtrosValores.localidad_id) params.localidad_id = filtrosValores.localidad_id;
      if (filtroVisible('telefono') && filtrosValores.telefono) params.telefono = filtrosValores.telefono;
      const response = await proveedoresAPI.getAll(params);
      if (response.success) {
        setProveedores(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaProveedoresRef.current = false;
      }
      if (seq === cargaProveedoresSeqRef.current) {
        setRefrescandoProveedores(false);
      }
    }
  }, [filtrosValores, filtroVisible]);

  const loadTipos = useCallback(async () => {
    try {
      const response = await tiposProveedoresAPI.getAll();
      if (response.success) setTipos(response.data);
    } catch (error) {
      console.error('Error al cargar tipos:', error);
    }
  }, []);

  const loadProvincias = useCallback(async () => {
    try {
      const response = await provinciasAPI.getAll();
      if (response.success) setProvincias(response.data);
    } catch (error) {
      console.error('Error al cargar provincias:', error);
    }
  }, []);

  const loadLocalidades = useCallback(async (provinciaId) => {
    if (!provinciaId) {
      setLocalidades([]);
      return;
    }
    try {
      const response = await localidadesAPI.getByProvincia(provinciaId);
      if (response.success) setLocalidades(response.data);
      else setLocalidades([]);
    } catch (error) {
      setLocalidades([]);
    }
  }, []);

  useEffect(() => {
    loadProveedores();
    loadTipos();
    loadProvincias();
  }, [loadProveedores, loadTipos, loadProvincias]);

  useEffect(() => {
    loadLocalidades(filtrosValores.provincia_id);
  }, [filtrosValores.provincia_id, loadLocalidades]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_PREFERENCIAS);
        if (cancelled) return;
        const raw = res.data;
        const data =
          raw?.valor ??
          (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.columnasVisibles != null ? raw : null) ??
          (Array.isArray(raw) ? raw[0]?.valor : null);
        if (data && typeof data === 'object') {
          if (data.columnasVisibles && typeof data.columnasVisibles === 'object') {
            setColumnasVisibles({ ...defaultColumnasVisibles, ...data.columnasVisibles });
          }
          if (data.filtrosVisibles && typeof data.filtrosVisibles === 'object') {
            setFiltrosVisibles({ ...defaultFiltrosVisibles, ...data.filtrosVisibles });
          }
          if (Array.isArray(data.ordenColumnas) && data.ordenColumnas.length > 0) {
            const knownIds = new Set(COLUMNAS_DEFINICION.map((c) => c.id));
            const saved = data.ordenColumnas.filter((id) => knownIds.has(id));
            const missing = defaultOrdenColumnas.filter((id) => !saved.includes(id));
            setOrdenColumnas([...saved, ...missing]);
          }
          // filtrosValores: solo sessionStorage (sobrevive volver desde movimientos / acciones).
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferenciasDesdeLista = useCallback(async (snapshot) => {
    const payload = {
      columnasVisibles: snapshot?.columnasVisibles ? { ...snapshot.columnasVisibles } : { ...columnasVisibles },
      filtrosVisibles: snapshot?.filtrosVisibles ? { ...snapshot.filtrosVisibles } : { ...filtrosVisibles },
      ordenColumnas: snapshot?.ordenColumnas ? [...snapshot.ordenColumnas] : [...ordenColumnas],
    };
    if (snapshot?.columnasVisibles) setColumnasVisibles(payload.columnasVisibles);
    if (snapshot?.filtrosVisibles) setFiltrosVisibles(payload.filtrosVisibles);
    if (snapshot?.ordenColumnas) setOrdenColumnas(payload.ordenColumnas);
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(CLAVE_PREFERENCIAS, payload);
      toast.success('Preferencias de columnas y filtros guardadas.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar preferencias');
    } finally {
      setSavingPreferencias(false);
    }
  }, [columnasVisibles, filtrosVisibles, ordenColumnas]);

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter(id => colVisible(id)),
    [ordenColumnas, columnasVisibles]
  );

  const proveedoresOrdenados = useMemo(() => {
    if (!sortByColumn) return proveedores;
    const a = [...proveedores];
    a.sort((p1, p2) => {
      const v1 = getSortValue(p1, sortByColumn);
      const v2 = getSortValue(p2, sortByColumn);
      const isNum = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (isNum) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return a;
  }, [proveedores, sortByColumn, sortAsc]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc(prev => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const getColumnLabel = (columnId) => COLUMNAS_DEFINICION.find(c => c.id === columnId)?.label || columnId;

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: 'proveedores',
        sheetName: 'Proveedores',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: proveedoresOrdenados,
        getCellValue: (p, colId) => valorCeldaProveedorExport(p, colId),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const generarPDFListado = () => {
    try {
      imprimirListadoColumnas({
        titulo: 'Listado de proveedores',
        subtitulo: `Generado el ${formatFecha(fechaHoy())}`,
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: proveedoresOrdenados,
        getCellValue: (p, colId) => valorCeldaProveedorExport(p, colId),
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const handleToggleActivo = async (proveedor) => {
    const nuevoEstado = !proveedor.activo;
    const texto = nuevoEstado ? '¿Activar este proveedor?' : '¿Desactivar este proveedor?';
    if (!window.confirm(texto)) return;
    try {
      const response = await proveedoresAPI.update(proveedor.id, { activo: nuevoEstado });
      if (response.success) {
        toast.success(nuevoEstado ? 'Proveedor activado' : 'Proveedor desactivado');
        loadProveedores();
      }
    } catch (error) {
      toast.error(error.message || 'Error al actualizar proveedor');
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes clientes-con-listado pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Proveedores</h1>
        {hasPermission('crear_clientes') && (
          <Link to="/proveedores/nuevo" className="btn-primary">Nuevo Proveedor</Link>
        )}
      </div>

      <div className="proveedores-listado clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
            {filtroVisible('cuit') && (
              <input
                type="text"
                placeholder="CUIT"
                value={filtrosValores.cuit}
                onChange={(e) => setFiltro('cuit', e.target.value)}
                className="search-input"
                style={{ maxWidth: 140 }}
              />
            )}
            {filtroVisible('razon_social') && (
              <>
                <input
                  type="text"
                  placeholder="Razón social"
                  value={filtrosValores.razon_social}
                  onChange={(e) => setFiltro('razon_social', e.target.value)}
                  className="search-input"
                  style={{ maxWidth: 180 }}
                />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={!!filtrosValores.razon_social_prefix}
                    onChange={(e) => setFiltro('razon_social_prefix', e.target.checked)}
                  />
                  Desde 1ª letra
                </label>
              </>
            )}
            {filtroVisible('estado') && (
              <select value={filtrosValores.activo} onChange={(e) => setFiltro('activo', e.target.value)} title="Estado">
                <option value="true">Solo activos</option>
                <option value="false">Solo inactivos</option>
                <option value="">Todos</option>
              </select>
            )}
            {filtroVisible('tipo') && (
              <select value={filtrosValores.tipo_id} onChange={(e) => setFiltro('tipo_id', e.target.value)}>
                <option value="">Tipo</option>
                {tipos.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            )}
            {filtroVisible('direccion') && (
              <input
                type="text"
                placeholder="Dirección"
                value={filtrosValores.direccion}
                onChange={(e) => setFiltro('direccion', e.target.value)}
                style={{ maxWidth: 140, padding: '6px 8px' }}
              />
            )}
            {filtroVisible('provincia') && (
              <select value={filtrosValores.provincia_id} onChange={(e) => { setFiltro('provincia_id', e.target.value); setFiltro('localidad_id', ''); }}>
                <option value="">Provincia</option>
                {provincias.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            )}
            {filtroVisible('localidad') && (
              <select value={filtrosValores.localidad_id} onChange={(e) => setFiltro('localidad_id', e.target.value)} disabled={!filtrosValores.provincia_id}>
                <option value="">Localidad</option>
                {localidades.map(l => (
                  <option key={l.id} value={l.id}>{l.nombre}</option>
                ))}
              </select>
            )}
            {filtroVisible('telefono') && (
              <input
                type="text"
                placeholder="Teléfono"
                value={filtrosValores.telefono}
                onChange={(e) => setFiltro('telefono', e.target.value)}
                style={{ maxWidth: 120, padding: '6px 8px' }}
              />
            )}
        </div>
        <div className="filters-toolbar filters-toolbar--acciones">
              <button type="button" className="btn-action-icon" onClick={() => setFiltrosValores({ ...defaultFiltrosValores })} title="Limpiar todos los filtros">
                <span aria-hidden="true">🧹</span>
              </button>
              <SelectorColumnas
                columnas={COLUMNAS_DEFINICION}
                visibleIds={Object.keys(columnasVisibles).filter(id => columnasVisibles[id])}
                ordenColumnas={ordenColumnas}
                onOrdenChange={setOrdenColumnas}
                onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
                onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
                onSave={guardarPreferenciasDesdeLista}
                titulo="Columnas visibles"
                labelBoton="Columnas"
              />
              <SelectorFiltros
                filtros={COLUMNAS_DEFINICION}
                visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id])}
                onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !filtrosVisibles[id] }))}
                onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
                onSave={guardarPreferenciasDesdeLista}
                titulo="Filtros visibles"
                labelBoton="Filtros"
              />
              {proveedoresOrdenados.length > 0 && (
                <ExportActions
                  onCsv={() => handleExportListado('csv')}
                  onExcel={() => handleExportListado('excel')}
                  onPrint={generarPDFListado}
                  printLabel="PDF"
                />
              )}
        </div>
        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className={`listado-tabla-area clientes-listado-tabla-area${refrescandoProveedores ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {ordenColumnasVisible.map(colId => (
                    <th
                      key={colId}
                      className="th-sortable"
                      onClick={() => handleSortColumn(colId)}
                      title={`Ordenar por ${getColumnLabel(colId)}. Clic de nuevo invierte el orden.`}
                    >
                      <span>{getColumnLabel(colId)}</span>
                      {sortByColumn === colId && (
                        <span className="sort-indicator" aria-hidden="true">
                          {sortAsc ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </th>
                  ))}
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proveedoresOrdenados.length === 0 ? (
                  <tr>
                    <td colSpan={1 + ordenColumnasVisible.length} className="no-data">No hay proveedores que coincidan con el filtro</td>
                  </tr>
                ) : (
                  proveedoresOrdenados.map(proveedor => (
                    <tr key={proveedor.id} className="table-row">
                      {ordenColumnasVisible.map(colId => {
                        if (colId === 'cuit') return <td key={colId}><strong>{proveedor.cuit}</strong></td>;
                        if (colId === 'razon_social') return (
                          <td key={colId}>
                            <div style={{ fontWeight: '500', color: '#2c3e50' }}>{proveedor.razon_social}</div>
                            {proveedor.email && (
                              <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>{proveedor.email}</div>
                            )}
                          </td>
                        );
                        if (colId === 'estado') return (
                          <td key={colId}>
                            <span className={`badge ${proveedor.activo ? 'activo' : 'inactivo'}`}>
                              {proveedor.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                        );
                        if (colId === 'tipo') return (
                          <td key={colId}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: '#e8f4f8',
                              color: '#2c3e50'
                            }}>
                              {proveedor.tipo_nombre || '-'}
                            </span>
                          </td>
                        );
                        if (colId === 'direccion') return <td key={colId}>{proveedor.direccion || '-'}</td>;
                        if (colId === 'provincia') return <td key={colId}>{proveedor.provincia_nombre || '-'}</td>;
                        if (colId === 'localidad') return <td key={colId}>{proveedor.localidad_nombre || '-'}</td>;
                        if (colId === 'telefono') return <td key={colId}>{proveedor.telefono || '-'}</td>;
                        if (colId === 'saldo') {
                          const saldo = parseFloat(proveedor.saldo) || 0;
                          return (
                            <td key={colId} style={{ fontWeight: '600', color: saldo > 0 ? '#c0392b' : saldo < 0 ? '#27ae60' : '#333' }}>
                              ${saldo.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          );
                        }
                        return <td key={colId}>-</td>;
                      })}
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                          {proveedor.activo ? (
                            <>
                              {hasPermission('ver_movimientos') && (
                                <Link
                                  to={`/proveedores/${proveedor.id}/movimientos`}
                                  className="btn-action-icon"
                                  title="Ver movimientos"
                                >
                                  <span aria-hidden="true">📋</span>
                                </Link>
                              )}
                              {hasPermission('crear_debitos') && (
                                <Link
                                  to="/movimientos/nuevo"
                                  state={{ modoProveedores: true, proveedorId: proveedor.id }}
                                  className="btn-action-icon"
                                  title="Nuevo movimiento (débito)"
                                >
                                  <span aria-hidden="true">💵</span>
                                </Link>
                              )}
                              {(hasPermission('crear_creditos') && hasPermission('crear_debitos')) && (
                                <Link
                                  to="/movimientos/nuevo"
                                  state={{ nuevoPago: true, proveedorId: proveedor.id }}
                                  className="btn-action-icon"
                                  title="Nueva orden de pago"
                                >
                                  <span aria-hidden="true">💸</span>
                                </Link>
                              )}
                              {hasPermission('editar_clientes') && (
                                <Link to={`/proveedores/editar/${proveedor.id}`} className="btn-action-icon btn-action-edit" title="Editar proveedor">
                                  <span aria-hidden="true">✏️</span>
                                </Link>
                              )}
                            </>
                          ) : (
                            hasPermission('editar_clientes') && (
                              <button type="button" onClick={() => handleToggleActivo(proveedor)} className="btn-action-icon btn-action-activar" title="Activar proveedor">
                                <span aria-hidden="true">▶️</span>
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {refrescandoProveedores && (
            <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
              <span className="listado-refresh-spinner clientes-listado-refresh-spinner" aria-hidden="true" />
              <span className="listado-refresh-label clientes-listado-refresh-label">Actualizando…</span>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Proveedores;
